import { useCallback, useEffect, useRef, useState } from 'react';
import MailNotifyStatusCard from './MailNotifyStatusCard';
import MailRuleForm from './MailRuleForm';
import MailRuleSection, { MAX_RULES } from './MailRuleSection';
import MailRuleTemplates from './MailRuleTemplates';
import OutlookConnectorCard, { useOutlookLink } from './OutlookConnectorCard';
import MailNotifyGuide, { useMailNotifyGuide } from './MailNotifyGuide';
import { InfoIcon } from './mailNotifyIcons';
import { FALLBACK } from './mailNotifyStatus';
import { useMailNotify } from './useMailNotify';
import { createMailRuleV2, deleteMailRuleV2, setMailRuleEnabledV2 } from '../utils/api';
import { SETTINGS_CLOSED_EVENT } from '../utils/settingsModal';
import { refreshTokenIfExpiringSoon } from '../services/microsoftIntegrationService';
import './MailNotificationView.css';
import './MailNotifyV2.css';

/** 背景同步 Outlook 連結狀態與通知狀態的間隔（10-20 使用者裁決：每分鐘） */
export const BACKGROUND_SYNC_MS = 60 * 1000;
/** 新增表單開著時，登入 token 剩不到這麼久就主動換新（10-20） */
export const DRAFT_TOKEN_REFRESH_MS = 10 * 60 * 1000;

/**
 * 信件即時通知（新架構，SA v2.0）。規則存在 MyAgent，經 mygenieap 代理 APISIX 讀寫；MyBro 只做設定畫面。
 *
 * 版面沿用舊版（D-1007-xx）：依使用者要做的順序由上而下，還沒輪到的步驟不出現。
 * 1. Outlook 連結——只在未連結、失效或查不到時出現
 * 2. Outlook即時通知狀態卡——<b>沒有訂閱按鈕</b>，存規則就是訂閱（D-1013-01）
 * 3. 我的規則（左欄）與新增規則（右欄）——<b>Outlook 已連結且狀態讀取成功時</b>出現（A-02）。
 *    不再等「已訂閱」：沒有規則就不會有訂閱，規則區必須先出現使用者才有辦法開始
 *
 * 狀態與規則來自同一支 GET（useMailNotify），寫入後由它重讀，三塊永遠一致。
 *
 * 刷新沿用 AGUI-1192：點選單整頁重新掛載；App 切回前景、設定視窗關閉時重抓 Outlook 連結與通知狀態，
 * 重抓期間狀態卡「同步中」、規則區先收起。⛔ 新增表單開著時不重抓，免得填到一半的內容被清掉。
 *
 * @param {boolean} [autoRebuild] MyAgent 是否已完成過期自動重建（/web/config 的 mailNotifyAutoRebuild，SA §7 S-04）
 */
export default function MailNotificationViewV2({ isMobile = false, autoRebuild = false }) {
  const mailNotify = useMailNotify();
  const { data, syncing, loadError, busy, refresh, sync, write, clearReportedError } = mailNotify;
  const {
    link: outlookLink, checking: checkingLink, refresh: refreshOutlookLink, sync: syncOutlookLink,
    check: checkOutlookLink, apply: applyOutlookLink,
  } = useOutlookLink();
  const guide = useMailNotifyGuide();
  // null＝沒在新增；{ template }＝表單開著（template 為 null 表示空白表單）
  const [draft, setDraft] = useState(null);
  // 每次打開表單都遞增，換範本時表單重新掛載、不殘留上一個範本的內容
  const [draftSeq, setDraftSeq] = useState(0);
  const draftOpenRef = useRef(false);
  draftOpenRef.current = draft !== null;

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // 表單開著不重抓；但可能在背景放了很久，先補一次登入續期
      if (draftOpenRef.current) {
        keepAliveDraftRef.current();
        return;
      }
      refreshOutlookLink();
      refresh();
    };
    // 從「前往設定」連好 Outlook 回來：連結狀態由 useOutlookLink 自己重查，這裡重抓通知狀態（SA §2.1）
    const onSettingsClosed = () => {
      if (!draftOpenRef.current) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(SETTINGS_CLOSED_EVENT, onSettingsClosed);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(SETTINGS_CLOSED_EVENT, onSettingsClosed);
    };
  }, [refreshOutlookLink, refresh]);

  // AGUI-1259：Outlook 連結還在重查（可能沿用上次的「已連結」）時也先不顯示，查回來才決定，避免規則區閃出又消失
  // 每分鐘在背景同步 Outlook 連結狀態與通知狀態（10-20 使用者裁決）：使用者在別處（MyAgent、對話、設定、其他分頁）
  // 改了，不必重新整理就看得到。只在頁面看得見時跑；新增表單開著、處理中（已有 3 秒輪詢）、重抓中時跳過這一輪。
  // 背景同步不顯示「同步中」、不收起規則區，失敗也不切成讀取失敗。
  const syncSkipRef = useRef(false);
  syncSkipRef.current = busy || syncing || checkingLink;

  /*
   * 新增表單開著時的登入續期（10-20 使用者裁決）：表單開著會跳過上面的同步，但使用者可能填很久。
   * 登入 token 剩不到 DRAFT_TOKEN_REFRESH_MS 就用 refresh token 換新，換完在背景查一次 Outlook 連結（不查訂閱）。
   * ⛔ 查到的連結狀態先存著，表單關閉才套用：查到「未連結」若立刻套用，規則區會連同填到一半的表單一起收掉。
   * 換新失敗靜默，交給按儲存那一次處理（儲存遇 401 會 refresh，連 refresh 都失敗才要求重新登入）。
   */
  const pendingLinkRef = useRef(null);
  const keepAliveRunningRef = useRef(false);
  const keepAliveDraft = useCallback(async () => {
    if (keepAliveRunningRef.current || busy) return;
    keepAliveRunningRef.current = true;
    try {
      if ((await refreshTokenIfExpiringSoon(DRAFT_TOKEN_REFRESH_MS)) !== 'refreshed') return;
      const next = await checkOutlookLink();
      if (draftOpenRef.current) pendingLinkRef.current = next;
      else applyOutlookLink(next);
    } catch {
      // 查不到就維持目前狀態，下一輪再試
    } finally {
      keepAliveRunningRef.current = false;
    }
  }, [busy, checkOutlookLink, applyOutlookLink]);
  const keepAliveDraftRef = useRef(keepAliveDraft);
  keepAliveDraftRef.current = keepAliveDraft;

  useEffect(() => {
    if (draft !== null || pendingLinkRef.current === null) return;
    const next = pendingLinkRef.current;
    pendingLinkRef.current = null;
    applyOutlookLink(next);
  }, [draft, applyOutlookLink]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (draftOpenRef.current) {
        keepAliveDraftRef.current();
        return;
      }
      if (syncSkipRef.current) return;
      syncOutlookLink();
      sync();
    }, BACKGROUND_SYNC_MS);
    return () => clearInterval(timer);
  }, [syncOutlookLink, sync]);

  const showRules = outlookLink === 'linked' && !checkingLink && Boolean(data) && !syncing && !loadError;
  const rules = data?.rules || [];

  const openDraft = (template) => {
    setDraft({ template });
    setDraftSeq((n) => n + 1);
  };

  const submitRule = useCallback(
    (body, { onPending }) => write((v) => createMailRuleV2(body, v), { fallback: FALLBACK.save, onPending }),
    [write],
  );
  const toggleRule = useCallback(
    (rule) => write((v) => setMailRuleEnabledV2(rule.ruleId, !rule.enabled, v), { fallback: FALLBACK.toggle }),
    [write],
  );
  const removeRule = useCallback(
    (rule, onPending) => write((v) => deleteMailRuleV2(rule.ruleId, v), { fallback: FALLBACK.delete, onPending }),
    [write],
  );

  // 範本面板與新增表單共用同一格：電腦版在右欄，手機版插在「我的規則」之前（D-1001-12）
  const addPanel = showRules && (
    draft ? (
      <MailRuleForm
        key={draftSeq}
        isMobile={isMobile}
        initial={draft.template?.rule}
        focusId={draft.template?.focusId}
        senderMode={draft.template?.senderMode}
        submit={submitRule}
        busy={busy}
        onCancel={() => {
          setDraft(null);
          // 表單上的錯誤跟著消失，改由狀態卡顯示（D-1021-03）
          clearReportedError();
        }}
        onSaved={() => setDraft(null)}
      />
    ) : (
      <MailRuleTemplates ruleCount={rules.length} maxRules={MAX_RULES} onPick={openDraft} disabled={busy} />
    )
  );

  const titleRow = (
    <div className="mail-notification-view__title-row">
      <h1 className="mail-notification-view__title">信件即時通知</h1>
      {/* 10-19 使用者裁決：資訊圖示放在標題與 BETA 之間 */}
      <button type="button" className="mail-notify-guide-entry" aria-label="使用說明" title="使用說明"
        onClick={guide.show}>
        <InfoIcon />
      </button>
      <span className="beta-pill">BETA</span>
    </div>
  );

  return (
    <div className={`mail-notification-view mail-notification-view--v2${isMobile ? ' mail-notification-view--mobile' : ''}`}>
      {/* AGUI-1316：手機版左上角的側欄開關是固定定位，內容往上捲會從它底下穿過去。
          頂端放一條不透明的固定頁首條墊在開關下面，標題列放進這條、與開關同一行，位置和原本一樣 */}
      {isMobile && <div className="mail-notification-view__mobile-bar">{titleRow}</div>}
      <div className="mail-notification-view__inner">
        <div className="mail-notification-view__columns">
          <div className="mail-notification-view__column mail-notification-view__column--main">
            <header className="mail-notification-view__header">
              {!isMobile && titleRow}
              {/* 畫面已沒有「訂閱」動作，說明不再用這個詞（SA §7 S-01，10-19 使用者裁決） */}
              <p className="mail-notification-view__desc">
                設定條件後，收到符合的信件就透過 M+ 通知你，告別漏信焦慮，工作更安心。
              </p>
            </header>
            <section className="mail-notification-view__section">
              <div className="mail-notification-view__tasks">
                <OutlookConnectorCard link={outlookLink} onRefresh={refreshOutlookLink} isMobile={isMobile} />
                {/* 10-20 使用者裁決：只有 Outlook 已連結才顯示狀態卡；未連結、連結失效、查詢中、查不到都只留 Outlook 卡——
                    沒連結就不會有通知，狀態卡的「已啟用」只會誤導（沿用上次的「已連結」背景重查時照常顯示） */}
                {outlookLink === 'linked' && (
                  <MailNotifyStatusCard
                    mailNotify={mailNotify}
                    outlookLink={outlookLink}
                    checkingLink={checkingLink}
                    isMobile={isMobile}
                    showPhone={showRules}
                    autoRebuild={autoRebuild}
                  />
                )}
              </div>
            </section>
            {showRules && (
              <MailRuleSection
                onAdd={() => openDraft(null)}
                adding={draft !== null}
                rules={rules}
                state={busy ? 'PENDING' : data.state}
                busy={busy}
                isMobile={isMobile}
                onToggle={toggleRule}
                onRemove={removeRule}
                onReload={sync}
              />
            )}
          </div>

          {/* 表單開在右欄而非 overlay：填到一半仍看得到左欄已經有哪幾條規則。
              AGUI-1286：電腦版與手機版都放在這一格，手機版的「範本在規則之前」改由 CSS 排順序——
              以前手機版放進左欄，視窗縮放跨過斷點時表單換了位置、被 React 重建，填到一半的內容全部清空 */}
          {showRules && (
            <div className="mail-notification-view__column mail-notification-view__column--add">{addPanel}</div>
          )}
        </div>
      </div>
      <MailNotifyGuide open={guide.open} onClose={guide.close} />
    </div>
  );
}
